"use client";
import React, { createContext, useContext, useState } from "react";
import {BASE_URL} from '../config/config';

const AddressContext = createContext();
export const useAddress = () => useContext(AddressContext);

export const AddressProvider = ({ children }) => {
  const [addresses, setAddresses] = useState([]);

  // 🔑 Helpers
  const getToken = () => localStorage.getItem("token");
  const getUserId = () => localStorage.getItem("user_id");

  // 📌 Fetch all addresses for a user
  const fetchAddresses = async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      const res = await fetch(`${BASE_URL}/useraddresses/${userId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const data = await res.json();
      

      if (data.status === 200 && Array.isArray(data.data)) {
        setAddresses(
          data.data.map((a) => ({
            id: Number(a.id),
            name: a.name,
            phone: a.phone,
            countryCode: "+91",
            address: a.address,
            pin: a.pin,
            city: a.city,
            country: a.country,
            isPrimary: a.status === "1", // ✅ true if primary
          }))
        );
      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  // 📌 Get single address by ID
  const getAddressById = async (id) => {
    const token = getToken();
    if (!id || !token) return null;

    try {
      const res = await fetch(`${BASE_URL}/user-addresses/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.status === 200 && data.data) {
        return {
          id: Number(data.data.id),
          name: data.data.name,
          phone: data.data.phone,
          address: data.data.address,
          city: data.data.city,
          pin: data.data.pin,
          countryCode: "+91",
          isPrimary: data.data.status === "1", // ✅ toggle correctly
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching single address:", error);
      return null;
    }
  };

  // 📌 Add new address
  const addAddress = async (newAddr) => {
    const userId = getUserId();
    if (!userId) return;

    try {
      const res = await fetch(`${BASE_URL}/user-addresses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          user_id: userId,
          name: newAddr.name,
          phone: newAddr.phone,
          address: newAddr.address,
          city: newAddr.city,
          pin: newAddr.pin,
          status: newAddr.isPrimary ? "1" : "0", // ✅ handle primary
          create_audit_id: userId,
        }),
      });

      const data = await res.json();
      if (data.status === 201) fetchAddresses();
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };

  // 📌 Update address
  const updateAddress = async (id, updatedAddr) => {
    const userId = getUserId();
    if (!userId) return;

    try {
      const res = await fetch(`${BASE_URL}/user-addresses/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: updatedAddr.name,
          phone: updatedAddr.phone,
          address: updatedAddr.address,
          city: updatedAddr.city,
          pin: updatedAddr.pin,
          status: updatedAddr.isPrimary ? "1" : "0", // ✅ enforce primary
          update_audit_id: userId,
        }),
      });

      const data = await res.json();
      if (data.status === 200) fetchAddresses();
    } catch (error) {
      console.error("Error updating address:", error);
    }
  };

  // 📌 Delete address (soft delete)
  const deleteAddress = async (id) => {
    const userId = getUserId();
    const token = getToken();
    if (!userId || !token) return;

    try {
      const res = await fetch(`${BASE_URL}/user-addresses/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id: userId,
        }),
      });

      const data = await res.json();
      if (data.status === 200) fetchAddresses();
      else console.error("Delete failed:", data);
    } catch (error) {
      console.error("Error deleting address:", error);
    }
  };

  return (
    <AddressContext.Provider
      value={{
        addresses,
        fetchAddresses,
        addAddress,
        updateAddress,
        deleteAddress,
        getAddressById,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};
